import {
  Badge,
  Button,
  Container,
  Flex,
  Heading,
  Spinner,
  Stack,
  Text,
} from "@chakra-ui/react"
import { type QueryClient, useQuery } from "@tanstack/react-query"
import { createFileRoute, redirect, useNavigate } from "@tanstack/react-router"

import { OrganizationsService, type UserPublic } from "@/client"
import OrganizationMembers from "@/components/Organizations/OrganizationMembers"

export const Route = createFileRoute(
  "/_layout/organizations/$organizationId/members",
)({
  component: OrganizationMembersPage,
  beforeLoad: async ({ context, params }) => {
    const queryClient = (context as { queryClient: QueryClient }).queryClient
    const currentUser = queryClient.getQueryData<UserPublic>(["currentUser"])

    if (currentUser?.is_superuser) {
      return
    }

    try {
      await queryClient.fetchQuery({
        queryKey: ["organization", params.organizationId],
        queryFn: () =>
          OrganizationsService.getOrganization({
            orgId: params.organizationId,
          }),
      })
    } catch (error) {
      throw redirect({ to: "/organizations" })
    }
  },
})

function OrganizationMembersPage() {
  const { organizationId } = Route.useParams()
  const navigate = useNavigate()

  const {
    data: organization,
    isLoading,
    error,
  } = useQuery({
    queryKey: ["organization", organizationId],
    queryFn: () =>
      OrganizationsService.getOrganization({ orgId: organizationId }),
  })

  if (isLoading) {
    return (
      <Container maxW="6xl" py={10}>
        <Flex justify="center" align="center" minH="300px">
          <Spinner size="lg" />
        </Flex>
      </Container>
    )
  }

  if (error || !organization) {
    return (
      <Container maxW="6xl" py={10}>
        <Stack gap={4}>
          <Text color="red.500">Unable to load organization members.</Text>
          <Button
            alignSelf="flex-start"
            onClick={() => navigate({ to: "/organizations" })}
          >
            Back to organizations
          </Button>
        </Stack>
      </Container>
    )
  }

  return (
    <Container maxW="6xl" py={10}>
      <Stack gap={6}>
        <Button
          variant="ghost"
          alignSelf="flex-start"
          onClick={() => navigate({ to: "/organizations" })}
        >
          ← Back to organizations
        </Button>

        <Flex
          justify="space-between"
          align={{ base: "flex-start", md: "center" }}
          gap={4}
          wrap="wrap"
        >
          <Heading size="lg">{organization.name}</Heading>
          <Badge colorPalette="blue">{organization.slug}</Badge>
        </Flex>

        <OrganizationMembers
          orgId={organizationId}
          orgName={organization.name}
        />
      </Stack>
    </Container>
  )
}
